/**
 * V2 pipeline progress UI — flat indented todo list.
 *
 * Always shows the FULL plan; no focus mode, no breadcrumb. Each row is
 * one node with a status checkbox icon, title, file path (if leaf), and
 * a progress fraction for groups. The currently-running node also shows
 * a streaming preview of the LLM's partial output underneath.
 *
 *   ▸ Pipeline root                  done · 11/12
 *     ✓ Design docs                  done · 5/5
 *       ✓ design/overview.md         3.4KB
 *       ✓ design/data-model.md       2.1KB
 *       ✓ design/protocol.md         4.5KB
 *     ◐ Tests                       running · 2/4
 *       ✓ shared/__tests__/constants.spec.ts
 *       ✓ client/__tests__/Movement.spec.ts
 *       ◐ server/__tests__/Game.spec.ts            writing… 2.3KB
 *           ┊ {"decision":"file","path":"server/__test
 *           ┊ s/Game.spec.ts","newCode":"import { desc
 *           ┊ ribe, it, expect } from 'vitest';\\n\\nimp
 *       ☐ shared/__tests__/messages.spec.ts
 *     ☐ Code
 */

import { useMemo } from 'react';
import { useAgent } from '../agentContextCore';
import type { V2NodeStatus, V2TreeNode, V2TreeState } from '../types';

export function V2PipelineTreeView() {
  const { state } = useAgent();
  const tree = state.v2Tree;
  if (!tree || !tree.rootId) return null;

  const root = tree.nodes[tree.rootId];
  if (!root) return null;

  const overall = useMemo(() => {
    const all = Object.values(tree.nodes);
    const leaves = all.filter(n => n.kind === 'file' || n.kind === 'designSpec');
    const done = leaves.filter(n => n.status === 'done').length;
    const specs = all.filter(n => n.kind === 'designSpec' && n.status === 'done').length;
    const failed = all.filter(n => n.status === 'failed').length;
    // Grand token total = sum of every node's OWN usage (no double-count).
    let inTok = 0, outTok = 0, cachedTok = 0, estimated = false;
    for (const n of all) {
      inTok += n.inputTokens ?? 0;
      outTok += n.outputTokens ?? 0;
      cachedTok += n.cachedInputTokens ?? 0;
      if (n.tokensEstimated) estimated = true;
    }
    return { leaves: leaves.length, done, specs, failed, inTok, outTok, cachedTok, estimated };
  }, [tree.nodes]);

  return (
    <div className="v2-todo" data-testid="v2-pipeline-tree">
      <div className="v2-todo-header">
        <span className="v2-todo-title">AI pipeline</span>
        <span className="v2-todo-summary">
          <span className={`v2-todo-stat ${tree.active ? 'running' : 'idle'}`}>
            {tree.active ? 'running' : '✓ done'}
          </span>
          <span className="v2-todo-stat">{overall.done}/{overall.leaves} items</span>
          {overall.specs > 0 && (
            <span className="v2-todo-stat">{overall.specs} specs</span>
          )}
          {overall.failed > 0 && (
            <span className="v2-todo-stat failed">{overall.failed} failed</span>
          )}
          {(overall.inTok > 0 || overall.outTok > 0) && (
            <span
              className="v2-todo-stat v2-todo-tokens-total"
              title={`Total: ${overall.inTok.toLocaleString()} input + ${overall.outTok.toLocaleString()} output = ${(overall.inTok + overall.outTok).toLocaleString()} tokens${overall.estimated ? ' (some estimated)' : ''}`}
            >
              Σ {overall.estimated ? '~' : ''}↑{fmtTok(overall.inTok)} ↓{fmtTok(overall.outTok)} tok
            </span>
          )}
          {overall.cachedTok > 0 && (
            <span
              className="v2-todo-stat v2-todo-tokens-cached"
              title={`${overall.cachedTok.toLocaleString()} of ${overall.inTok.toLocaleString()} input tokens were prompt-cache HITS (billed ~cheap). Effective (uncached) input ≈ ${(overall.inTok - overall.cachedTok).toLocaleString()} (${overall.inTok > 0 ? Math.round((overall.cachedTok / overall.inTok) * 100) : 0}% cached).`}
            >
              ⚡{fmtTok(overall.cachedTok)} cached ({overall.inTok > 0 ? Math.round((overall.cachedTok / overall.inTok) * 100) : 0}%)
            </span>
          )}
        </span>
      </div>

      <div className="v2-todo-body" data-testid="v2-todo-body">
        <V2TodoRow node={root} tree={tree} />
      </div>
    </div>
  );
}

interface V2TodoRowProps {
  node: V2TreeNode;
  tree: V2TreeState;
}

function V2TodoRow({ node, tree }: V2TodoRowProps) {
  const stats = useMemo(() => subtreeStats(node, tree.nodes), [node, tree.nodes]);
  const isLeaf = node.kind === 'file' || node.kind === 'designSpec' || node.kind === 'pending';
  const isActive = node.status === 'planning' || node.status === 'running';

  return (
    <div
      className={`v2-todo-row depth-${node.depth} status-${node.status} kind-${node.kind}${isActive ? ' active' : ''}`}
      data-testid="v2-todo-row"
      data-node-id={node.id}
      data-node-status={node.status}
      data-node-kind={node.kind}
    >
      <div className="v2-todo-line">
        <V2TodoIndent depth={node.depth} />
        <V2TodoCheckbox status={node.status} />
        <span className="v2-todo-text">
          <span className="v2-todo-name">
            {node.kind === 'designSpec' && node.designSpec ? node.designSpec.title : node.title}
          </span>
          {node.phase && node.depth <= 1 && (
            <span className={`v2-todo-phase-pill phase-${node.phase}`}>{node.phase}</span>
          )}
          {node.kind === 'designSpec' && node.designSpec && (
            <span className="v2-todo-spec-cat">{node.designSpec.category}</span>
          )}
          {node.kind === 'designSpec' && node.designSpec?.systemName && (
            <span className="v2-todo-spec-sys">{node.designSpec.systemName}</span>
          )}
          {node.kind === 'file' && node.filePath && node.filePath !== node.title && (
            <span className="v2-todo-path">{node.filePath}</span>
          )}
          {node.fileContentLength !== undefined && (
            <span className="v2-todo-size">{formatKB(node.fileContentLength)}</span>
          )}
          {(stats.inTok > 0 || stats.outTok > 0) && (
            <span
              className="v2-todo-tokens"
              title={`${stats.inTok.toLocaleString()} input + ${stats.outTok.toLocaleString()} output tokens${stats.estimated ? ' (estimated)' : ''}`}
            >
              {stats.estimated ? '~' : ''}↑{fmtTok(stats.inTok)} ↓{fmtTok(stats.outTok)}
            </span>
          )}
        </span>
        {!isLeaf && stats.leaves > 0 && (
          <span className="v2-todo-progress">
            {stats.done}/{stats.leaves}
            {stats.failed > 0 && <span className="failed"> · {stats.failed} failed</span>}
          </span>
        )}
        {isActive && node.liveChunkLength !== undefined && (
          <span className="v2-todo-live-meta">
            writing… {formatKB(node.liveChunkLength)}
          </span>
        )}
      </div>

      {/* Reasoning block — for fix-loop nodes (diagnose + each fix attempt),
          show WHAT it's fixing (the failing test + error) and WHY (the AI's
          hypothesis), as bullet lines, so the user can follow the fix loop
          without reading the raw stream. Carried in node.purpose. */}
      {node.phase === 'fix' && node.purpose && (
        <V2Reasoning depth={node.depth} purpose={node.purpose} />
      )}

      {/* Live activity — the granular "what's it doing right now" narration
          (clustering, tracing the import graph, asking the model, applying a
          search-replace edit, re-running tests…). Shows the running sub-steps
          while active, and the final step once done. */}
      {node.activity && (
        <V2Activity depth={node.depth} node={node} active={isActive} />
      )}

      {/* Stream preview — only on the currently-active leaf. */}
      {isActive && node.liveChunk && (
        <V2LiveChunk depth={node.depth} text={node.liveChunk} />
      )}

      {node.errorMessage && (
        <div className="v2-todo-error" data-testid="v2-todo-error">
          <V2TodoIndent depth={node.depth + 1} />
          <span>⚠ {node.errorMessage}</span>
        </div>
      )}

      {node.childIds.map(childId => {
        const child = tree.nodes[childId];
        if (!child) return null;
        return <V2TodoRow key={child.id} node={child} tree={tree} />;
      })}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function V2TodoIndent({ depth }: { depth: number }) {
  if (depth === 0) return null;
  return (
    <span className="v2-todo-indent" aria-hidden>
      {Array.from({ length: depth }, (_, i) => (
        <span key={i} className="v2-todo-indent-bar" />
      ))}
    </span>
  );
}

function V2TodoCheckbox({ status }: { status: V2NodeStatus }) {
  switch (status) {
    case 'pending':  return <span className="v2-todo-check pending"  title="pending">☐</span>;
    case 'planning': return <span className="v2-todo-check planning" title="planning">◐</span>;
    case 'running':  return <span className="v2-todo-check running"  title="running"><span className="v2-todo-spinner" /></span>;
    case 'done':     return <span className="v2-todo-check done"     title="done">☑</span>;
    case 'failed':   return <span className="v2-todo-check failed"   title="failed">☒</span>;
    default:         return <span className="v2-todo-check pending"  title="?">·</span>;
  }
}

/**
 * Reasoning bullets for a fix-loop node: each '\n'-separated line of the node's
 * `purpose` rendered as its own dimmed row. For a fix attempt these are the
 * AI's hypothesis (Bug N:) + the failing test(s) it targets; for the
 * diagnose node, what it's reading + which tests it's diagnosing.
 */
function V2Reasoning({ depth, purpose }: { depth: number; purpose: string }) {
  const lines = useMemo(
    () => purpose.split('\n').map(l => l.trim()).filter(Boolean),
    [purpose],
  );
  if (lines.length === 0) return null;
  return (
    <div className="v2-todo-reason" data-testid="v2-todo-reason">
      <V2TodoIndent depth={depth + 1} />
      <div className="v2-todo-reason-text">
        {lines.map((line, i) => (
          <div key={i} className="v2-todo-reason-line">{line}</div>
        ))}
      </div>
    </div>
  );
}

/**
 * Live activity narration for a node: the granular sub-step messages the
 * pipeline emits (`node:activity`). While the node is active we show the last
 * few steps (so the user sees the sequence and that it's progressing even when
 * a reasoning model is thinking silently); once done, just the final step.
 */
function V2Activity({ depth, node, active }: { depth: number; node: V2TreeNode; active: boolean }) {
  const lines = useMemo(() => {
    const log = node.activityLog ?? (node.activity ? [node.activity] : []);
    return active ? log.slice(-4) : log.slice(-1);
  }, [node.activityLog, node.activity, active]);
  if (lines.length === 0) return null;
  return (
    <div className="v2-todo-activity" data-testid="v2-todo-activity">
      <V2TodoIndent depth={depth + 1} />
      <div className="v2-todo-activity-text">
        {lines.map((line, i) => {
          const isCurrent = i === lines.length - 1;
          return (
            <div
              key={i}
              className={`v2-todo-activity-line${isCurrent ? ' current' : ''}`}
            >
              {active && isCurrent && <span className="v2-todo-activity-dot" aria-hidden />}
              {line}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tail of the live LLM output. Capped at the last ~6 lines so the panel
 * doesn't expand uncontrollably as content arrives.
 */
function V2LiveChunk({ depth, text }: { depth: number; text: string }) {
  const lines = useMemo(() => text.split('\n').slice(-6), [text]);
  return (
    <div className="v2-todo-live" data-testid="v2-todo-live">
      <V2TodoIndent depth={depth + 1} />
      <div className="v2-todo-live-text">
        {lines.map((line, i) => (
          <div key={i} className="v2-todo-live-line">
            {line || ' '}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function formatKB(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  return `${(bytes / 1024).toFixed(1)}KB`;
}

interface SubtreeStats {
  leaves: number;
  done: number;
  failed: number;
  inTok: number;
  outTok: number;
  estimated: boolean;
}

function subtreeStats(
  node: V2TreeNode,
  allNodes: Record<string, V2TreeNode>,
): SubtreeStats {
  const stats: SubtreeStats = { leaves: 0, done: 0, failed: 0, inTok: 0, outTok: 0, estimated: false };
  const walk = (n: V2TreeNode) => {
    if (n.kind === 'file') {
      stats.leaves += 1;
      if (n.status === 'done') stats.done += 1;
      else if (n.status === 'failed') stats.failed += 1;
    }
    // Tokens land on whichever node made the LLM call (leaves + fix/diagnose
    // nodes). Sum each node's OWN usage across the subtree (no double-count).
    stats.inTok += n.inputTokens ?? 0;
    stats.outTok += n.outputTokens ?? 0;
    if (n.tokensEstimated) stats.estimated = true;
    for (const cid of n.childIds) {
      const c = allNodes[cid];
      if (c) walk(c);
    }
  };
  walk(node);
  return stats;
}

/** Compact token count: 1234 → "1.2k". */
function fmtTok(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
}
